import { DefaultQueryParams } from '@frontend/api-utils';
import { ChartClient } from '@frontend/chart/api';
import { IotsUptimeLineChart } from '@frontend/chart/components';
import { Chart, ChartBinSize, ChartQueryParam } from '@frontend/chart/types';
import { useEffect, useMemo, useState } from 'react';

import { useInsightFetchRange } from '../../../analytics/insights/insight-detail/use-insight-fetch-range';
import { getLast30DaysChartRange } from '../../pudo/last-30-days-chart-range';
import { Last30DaysHardwareIotsUptimeLineChartProps } from './last-30-days-hardware-iots-uptime-line-chart.component';

export const useLast30DaysHardwareIotsUptimeLineChart = (props: Last30DaysHardwareIotsUptimeLineChartProps) => {
    const [data, setData] = useState<Chart | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);

    const fetchRange = useInsightFetchRange(getLast30DaysChartRange, { defaultBinSize: ChartBinSize.DAY, useDetailBinSize: true });

    useEffect(() => {
        if (!props.accountId) {
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);

        const { after, before } = fetchRange;

        ChartClient.fetchChartData(props.accountId, IotsUptimeLineChart.definition, {
            [DefaultQueryParams.AFTER]: after,
            [DefaultQueryParams.BEFORE]: before,
            ...fetchRange.entityParams,
            [ChartQueryParam.BIN_SIZE]: fetchRange.binSize!
        })
            .then((chart) => {
                if (!cancelled) {
                    setData(chart);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setData(undefined);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [props.accountId, fetchRange.key]);

    const averageUptime = useMemo(() => {
        if (!data) {
            return null;
        }

        const points = data.series.flatMap((series) => series.data);
        if (points.length === 0) {
            return null;
        }

        return Math.round(points.reduce((sum, point) => sum + point.value, 0) / points.length);
    }, [data]);

    return {
        data,
        isLoading,
        averageUptime
    };
};
